import { ACCOUNTING_SERVICE_STORE_CONFIGS } from "@intrasync/accounting";
import { ServiceStoreConfig } from "./types/ServiceStoreTypes";

// Import categorized service stores
import { aiStores } from "./serviceStores/ai.stores";
import { communicationStores } from "./serviceStores/communication.stores";
import { crmStores } from "./serviceStores/crm.stores";
import { departmentStores } from "./serviceStores/department.stores";
import { dispatchStores } from "./serviceStores/dispatch.stores";
import { fieldStores } from "./serviceStores/field.stores";
import { financialStores } from "./serviceStores/financial.stores";
import { hardwareStores } from "./serviceStores/hardware.stores";
import { jobStores } from "./serviceStores/job.stores";
import { manufacturingStores } from "./serviceStores/manufacturing.stores";
import { organizationDataStores } from "./serviceStores/organization-data.stores";
import { panelsStores } from "./serviceStores/panels.stores";
import { pdfEditorStores } from "./serviceStores/pdf-editor.stores";
import { schedulingStores } from "./serviceStores/scheduling.stores";
import { systemStores } from "./serviceStores/system.stores";
import { userStores } from "./serviceStores/user.stores";
import { yardStores } from "./serviceStores/yard.stores";
import { yardManagementStores } from "./serviceStores/yardManagement.stores";
import { inventoryManagementStores } from "./serviceStores/inventoryManagement.stores";
import { aiAgentStores } from "./serviceStores/aiAgent.stores";
import { uncategorizedStores } from "./serviceStores/uncategorized.stores";
import { estimatingStores } from "./serviceStores/estimating.stores";
/**
 * Main Service Store Configuration
 * Combines all categorized service stores into a single array
 *
 * Categories are organized based on the api-types directory structure:
 * - AI: AI services (OpenAI, etc.)
 * - Communication: Chat, support tickets, RFI, submittals
 * - CRM: Accounts, contacts, opportunities, orders
 * - Department: Organization structure
 * - Dispatch: Load and trailer management
 * - Field: Field operations, issues, warranty claims
 * - Financial: Invoicing, billing, change orders, PO approvals
 * - Hardware: Hardware orders and details
 * - Job: Project and job management
 * - Manufacturing: BOM, materials, products, QC, NCR
 * - Organization Data: List tracker
 * - Panels: Panel production, additional casting, remediation
 * - PDF Editor: Document management
 * - Scheduling: Schedule generation, holidays
 * - System: API services, Azure blob storage
 * - User: User management, certifications, training
 * - Yard: Yard inventory and management
 * - Uncategorized: Services without api-types yet
 */
const baseStores: ServiceStoreConfig[] = [
  // AI Services
  ...aiStores,

  // Communication Services
  ...communicationStores,

  // CRM Services
  ...crmStores,

  // Department Services
  ...departmentStores,

  // Dispatch Services
  ...dispatchStores,

  // Estimating Services
  ...estimatingStores,

  // Field Services
  ...fieldStores,

  // Financial Services
  ...financialStores,

  // Hardware Services
  ...hardwareStores,

  // Job Services
  ...jobStores,

  // Manufacturing Services
  ...manufacturingStores,

  // Organization Data Services
  ...organizationDataStores,

  // Panel Services
  ...panelsStores,

  // PDF Editor Services
  ...pdfEditorStores,

  // Scheduling Services
  ...schedulingStores,

  // System Services
  ...systemStores,

  // User Services
  ...userStores,

  // Yard Services
  ...yardStores,

  // Yard Management Services (@intrasync/yard-management — layout/canvas module)
  ...yardManagementStores,

  // Inventory Management Services (@intrasync/inventory-management — InventoryHub)
  ...inventoryManagementStores,

  // AI Agent Services (@intrasync/ai-agent — AgentHub)
  ...aiAgentStores,

  // Uncategorized Services (no api-types directory yet)
  ...uncategorizedStores,
];

/**
 * AccountingHub service stores sourced from `@intrasync/accounting`
 * (`ACCOUNTING_SERVICE_STORE_CONFIGS`) so they stay in sync with the package.
 *
 * Many of these names (invoices, bills, payments, vendors, gl-transactions,
 * etc.) are already registered above by the financial/crm modules with richer,
 * app-specific config. ServiceStore registration is first-wins/idempotent (see
 * StoreRegistry.registerServiceStore), so we filter out any name already
 * present in `baseStores` — the existing authoritative config keeps priority
 * and we only add the stores the hub needs that nothing else supplies.
 */
const existingStoreNames = new Set(baseStores.map((store) => store.name));
const accountingHubStores = (
  ACCOUNTING_SERVICE_STORE_CONFIGS as unknown as ServiceStoreConfig[]
).filter((store) => !existingStoreNames.has(store.name));

const serviceStores: ServiceStoreConfig[] = [
  ...baseStores,
  ...accountingHubStores,
];

export default serviceStores;
